import { useEffect, useState } from 'react';
import { useViewMode } from '@/shared/hooks/useViewMode';
import { Markdown } from '@/shared/ui/Markdown';
import { ViewModeToggle, type ViewModeOption } from '@/shared/ui/ViewModeToggle';
import { DevRequestLink } from '@/features/project-board/dev-request-link';
import { DependencyGraph } from '@/features/project-board/components/DependencyGraph';
import { QaScheduleEditor } from '@/features/project-board/components/QaScheduleEditor';
import { QaBriefSchedule } from '@/features/project-board/qa-schedule';
import { boardApi } from '@/features/project-board/api';
import type { DocumentContent } from '@/features/project-board/types';

export interface DrawerDoc {
  label: string;
  path: string;
}

/** 공유 링크(`item`/`kind` 쿼리)로 항목을 되찾을 때 어느 조회 함수를 탈지 가른다. */
export type PayloadKind = 'epic' | 'task' | 'issue' | 'incident' | 'dev-request' | 'qa' | 'ai-usage' | 'data-query';

/** 이 항목에 연결된 하위 항목(에픽 → task). 지라의 에픽-스토리 관계에 해당한다. */
export interface DrawerChild {
  key: string;
  label: string;
  status: string;
  statusToken: string;
  hint: string;
  /** 선행 task. 지라의 "blocked by"에 해당 — 목록에서 바로 보이게 컬럼으로 둔다. */
  dependsOn: string[];
  blockedBy: string[];
  /** 눌렀을 때 열 상세. 지연 생성이라 목록을 그릴 때 전부 만들지 않는다. */
  open: () => DrawerPayload;
}

/**
 * 이미 손에 있는 본문 — 경로로 받아 오는 `docs`와 달리 조회가 없다(개발요청 원문처럼 인입 정본이 곧 본문).
 *
 * `header`·`footer`는 드로어가 내용을 모르는 채 그대로 그린다. 첨부 링크·개정 이력처럼 그 화면만 아는
 * 것을 여기 실어 보내면, 드로어가 항목 종류마다 분기를 갖지 않아도 된다.
 */
export type DrawerBody = {
  label: string;
  header?: React.ReactNode;
  footer?: React.ReactNode;
} & ({ text: string; content?: never } | { text?: never; content: React.ReactElement });

export interface DrawerPayload {
  key: string;
  /** 항목 종류 — 공유 링크로 돌아왔을 때 `DrawerPayloads.resolveLinked`가 어느 색인 목록에서 찾을지 정한다. */
  kind: PayloadKind;
  /**
   * 이 항목이 온 하네스 저장소. 문서 조회에 필요하다 — 경로(`specs/…/PRD.md`)는 저장소마다 따로 있어서
   * 경로만으로는 어느 문서인지 정해지지 않는다. 한 항목의 문서는 전부 같은 저장소라 문서마다 두지 않는다.
   *
   * `body`로 오는 항목(개발요청)은 하네스에서 온 것이 아니라 저장소가 **없다** — 빈 문자열로 있는 척하지
   * 않고 비운다.
   */
  source?: string;
  title: string;
  meta: Array<{ label: string; value: React.ReactNode }>;
  docs: DrawerDoc[];
  /** 받아 올 것 없이 바로 그릴 본문. 있으면 문서 목록 대신 이것을 본문에 놓는다. */
  body?: DrawerBody;
  children?: { label: string; items: DrawerChild[] };
  /** 상위 항목(task → 에픽). 헤더에 링크로 올려 왕복 이동이 되게 한다. */
  parent?: { key: string; label: string; open: () => DrawerPayload };
}

type MainTab = 'children' | 'docs';

type ChildMode = 'list' | 'graph';

const CHILD_MODES: ReadonlyArray<ViewModeOption<ChildMode>> = [
  { key: 'list', label: '목록', glyph: '☰' },
  { key: 'graph', label: '선행 그래프', glyph: '⤳' },
];

/**
 * 개발요청 본문을 첨부 섹션(`## 첨부: <파일명>`) 경계로 나눈다.
 *
 * 원래는 본문 전체를 `<Markdown>` 하나에 통째로 넘겼는데, 첨부 안에 mermaid 다이어그램이 있으면
 * 운영에서만 렌더가 끝나지 않고 원문 코드로 남는 사례가 있었다(원인 미확인, 로컬 재현 불가).
 * 반면 에픽 문서 탭처럼 파일마다 별도 `<Markdown>` 인스턴스로 그리는 경로는 항상 정상 렌더된다 —
 * 그래서 본문도 같은 섹션 = 별도 인스턴스 구조로 맞춘다.
 */
function splitBodySections(text: string): string[] {
  return text.split(/\n(?=## 첨부: )/);
}

/** 개발요청 본문이 HTML 문서/조각인지 판별한다. 일반 Markdown 안의 꺾쇠괄호는 그대로 둔다. */
function isHtmlBody(text: string): boolean {
  return /<!doctype\s+html\b|<html(?:\s|>)|<(?:head|body|style|script|div|table|section|main|article|form)(?:\s|>)/i.test(
    text,
  );
}

/**
 * 첨부 섹션의 `## 첨부: <파일명>` 헤더 줄을 떼어낸다 — HTML 첨부는 이 줄까지 `srcDoc`에 밀어 넣으면
 * 마크다운 헤더 문법이 그대로 날것 텍스트로 iframe 위에 얹힌다. 떼어낸 파일명은 `HtmlPreview`가 따로
 * 보여준다.
 */
function splitAttachmentHeading(section: string): { name: string | null; rest: string } {
  const match = /^## 첨부: (.+)\n?/.exec(section);
  if (!match) return { name: null, rest: section };
  return { name: match[1], rest: section.slice(match[0].length) };
}

/**
 * HTML 요청을 실제 화면처럼 보여 주되 콘솔의 문서·쿠키·스크립트와 분리한다.
 * sandbox에 allow-scripts/allow-same-origin을 주지 않아 요청 본문이 실행 권한을 얻지 못한다.
 */
function HtmlPreview({ html, name }: { html: string; name?: string | null }): React.JSX.Element {
  return (
    <section className="mb-4">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold tracking-wide text-muted uppercase">
        <span>HTML 미리보기{name ? ` — ${name}` : ''}</span>
        <span className="font-normal normal-case text-faint">스크립트 실행 안 함</span>
      </div>
      <iframe
        title="개발요청 HTML 미리보기"
        srcDoc={html}
        sandbox=""
        className="min-h-[420px] w-full rounded-md border border-line bg-white"
      />
    </section>
  );
}

/**
 * 문서 목록 그룹핑 — 루트 문서(PRD·HLD·TASKS…)와 하위 폴더(`lld/`·`tasks/`)를 나눈다.
 * ad-system처럼 문서가 13개 넘는 에픽에서 목록이 그냥 긴 나열이 되지 않게 한다.
 */
function groupDocs(docs: DrawerDoc[]): Array<{ label: string; docs: DrawerDoc[] }> {
  const groups: Array<{ label: string; docs: DrawerDoc[] }> = [];
  for (const doc of docs) {
    const at = doc.label.indexOf('/');
    const label = at === -1 ? '' : doc.label.slice(0, at);
    const group = groups.find((candidate) => candidate.label === label);
    const target = group ?? { label, docs: [] };
    if (!group) groups.push(target);
    target.docs.push(at === -1 ? doc : { ...doc, label: doc.label.slice(at + 1) });
  }
  return groups;
}

/** 지라의 status lozenge — 상태를 색+대문자 라벨로 고정 폭 없이 보여준다. */
const LOZENGE: Record<string, string> = {
  todo: 'bg-line-soft text-muted',
  progress: 'bg-amber-100 text-amber-900',
  done: 'bg-emerald-100 text-emerald-900',
  dropped: 'bg-violet-100 text-violet-900',
  unknown: 'bg-orange-100 text-orange-900',
};

/**
 * 상세 패널 — 지라 이슈 화면 구성을 따른다:
 * 왼쪽에 본문(연결 항목 + 문서), 오른쪽에 세부 정보(필드 목록). 좁은 화면에서는 한 줄로 접힌다.
 */
export function DetailDrawer({
  payload,
  onClose,
  initialDocPath,
  onSelectionChange,
}: {
  payload: DrawerPayload | null;
  onClose: () => void;
  /** 공유 링크로 열렸을 때 처음부터 골라둘 문서 — 없으면 기존처럼 목록 첫 문서를 고른다. */
  initialDocPath?: string | null;
  /** 지금 드로어가 보여주는 것(드릴다운 포함) — 주소창에 그대로 반영해 링크로 공유되게 한다. */
  onSelectionChange?: (selection: { kind: PayloadKind; key: string; docPath: string | null } | null) => void;
}): React.JSX.Element | null {
  // 드릴다운 스택 — 에픽에서 task로 들어가도 되돌아올 수 있게 경로를 쌓는다.
  const [stack, setStack] = useState<DrawerPayload[]>([]);
  const [childMode, setChildMode] = useViewMode<ChildMode>('project-board.children.view', 'list');
  /** 본문에 무엇을 띄울지 — 연결 task와 문서를 한 화면에 겹치면 둘 다 좁아진다. */
  const [mainTab, setMainTab] = useState<MainTab>('children');
  const [activePath, setActivePath] = useState<string | null>(null);
  const [doc, setDoc] = useState<DocumentContent | null>(null);
  const [docError, setDocError] = useState<string | null>(null);

  const current = stack.at(-1) ?? payload;
  const source = current?.source ?? '';

  useEffect(() => {
    setStack(payload ? [payload] : []);
  }, [payload]);

  useEffect(() => {
    // 본문이 이미 있으면 문서를 고르지 않는다 — 고른 문서는 화면에 안 나오면서 조회만 나간다.
    // 공유 링크로 막 열린 최상위 항목이면 그 링크가 가리킨 문서를 먼저 쓴다.
    const fromLink =
      current === payload && initialDocPath && current?.docs.some((entry) => entry.path === initialDocPath)
        ? initialDocPath
        : null;
    setActivePath(current?.body ? null : (fromLink ?? current?.docs[0]?.path ?? null));
    // 링크가 문서를 지목했으면 그 문서를 보여준다 — 연결 task가 있어도 목록 탭 뒤에 숨기지 않는다.
    setMainTab(fromLink ? 'docs' : (current?.children?.items.length ?? 0) > 0 ? 'children' : 'docs');
  }, [current, payload]);

  useEffect(() => {
    onSelectionChange?.(current ? { kind: current.kind, key: current.key, docPath: current.body ? null : activePath } : null);
  }, [current, activePath]);

  useEffect(() => {
    if (!activePath) {
      setDoc(null);
      return;
    }
    setDoc(null);
    setDocError(null);
    boardApi
      .document(source, activePath)
      .then(setDoc)
      .catch((cause: Error) => setDocError(cause.message));
  }, [source, activePath]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // 폼 모달이 열린 동안 Escape는 최상위 모달이 처리한다. 뒤의 drawer와 선택 URL은 유지한다.
      if (event.key === 'Escape' && document.querySelector('[aria-modal="true"]') === null) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!payload || !current) return null;

  return (
    <>
      <div className="fixed inset-0 z-30 bg-[#1b1f24]/35" onClick={onClose} />
      <aside className="fixed top-0 right-0 z-40 flex h-screen w-[min(1480px,98vw)] flex-col border-l border-line bg-surface shadow-2xl">
        <header className="flex items-start gap-3 border-b border-line px-6 py-4">
          <div className="min-w-0">
            {stack.length > 1 && (
              <button
                onClick={() => setStack((items) => items.slice(0, -1))}
                className="mb-1 cursor-pointer text-[11.5px] text-accent hover:underline"
              >
                ← {stack[stack.length - 2].key}
              </button>
            )}
            {current.parent && (
              <button
                onClick={() => setStack((items) => [...items, current.parent!.open()])}
                className="mb-1 block cursor-pointer truncate text-[11.5px] text-accent hover:underline"
                title={current.parent.label}
              >
                ◈ {current.parent.label}
              </button>
            )}
            <div className="font-mono text-[11px] text-faint">{current.key}</div>
            <h2 className="mt-0.5 text-[17px] leading-snug font-semibold">{current.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="ml-auto cursor-pointer rounded-md border border-line px-2 py-1 text-muted hover:bg-line-soft"
          >
            ✕
          </button>
        </header>

        <div
          className={`grid min-h-0 flex-1 grid-cols-1 ${
            current.body ? 'lg:grid-cols-[1fr_320px]' : 'lg:grid-cols-[210px_1fr_320px]'
          }`}
        >
          {/* 문서 목록 — 가로 탭으로 두면 30개가 넘쳐 스크롤해야 했다. 목록은 전부 한눈에 보인다.
              본문이 이미 손에 있는 항목(개발요청·QA 티켓)은 고를 문서가 없어 이 칼럼을 내린다. */}
          <nav
            className={`min-h-0 flex-col overflow-y-auto border-r border-line bg-canvas p-2 ${
              current.body ? 'hidden' : 'hidden lg:flex'
            }`}
          >
            <div className="px-2 py-1.5 text-[11px] font-semibold tracking-wide text-muted uppercase">
              문서 {current.docs.length}
            </div>
            {current.docs.length === 0 && <div className="px-2 text-[11px] text-faint">연결된 문서 없음</div>}
            {groupDocs(current.docs).map((group) => (
              <div key={group.label} className="mb-1">
                {group.label && (
                  <div className="px-2 pt-2 pb-1 font-mono text-[10px] text-faint">{group.label}</div>
                )}
                {group.docs.map((entry) => (
                  <button
                    key={entry.path}
                    onClick={() => {
                      setActivePath(entry.path);
                      // 목록에서 문서를 고르는 행위 자체가 "문서를 보겠다"는 뜻이다.
                      setMainTab('docs');
                    }}
                    title={entry.path}
                    className={`block w-full truncate rounded-md px-2 py-1.5 text-left text-[12px] ${
                      entry.path === activePath
                        ? 'bg-accent-soft font-semibold text-accent'
                        : 'text-ink hover:bg-line-soft'
                    }`}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          {/* 본문 — 연결 항목 + 문서 */}
          <div className="flex min-h-0 min-w-0 flex-col">
            {current.children && current.children.items.length > 0 && (
              <div className="flex items-center gap-1 border-b border-line px-4 pt-3">
                {(
                  [
                    { key: 'children' as MainTab, label: `${current.children.label} ${current.children.items.length}` },
                    { key: 'docs' as MainTab, label: `문서 ${current.docs.length}` },
                  ]
                ).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setMainTab(tab.key)}
                    className={`-mb-px cursor-pointer border-b-2 px-3 py-2 text-[13px] ${
                      mainTab === tab.key
                        ? 'border-accent font-semibold text-accent'
                        : 'border-transparent text-muted hover:text-ink'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {mainTab === 'children' && current.children && current.children.items.length > 0 && (
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-[11px] text-faint">
                    {childMode === 'graph' ? '선행 관계로 배치' : '번호 오름차순'}
                  </span>
                  <span className="flex-1" />
                  <ViewModeToggle options={CHILD_MODES} value={childMode} onChange={setChildMode} />
                </div>

                {childMode === 'graph' ? (
                  <DependencyGraph
                    items={current.children.items}
                    onOpen={(child) => setStack((items) => [...items, child.open()])}
                  />
                ) : (
                  <>
                <div className="mb-1 grid grid-cols-[36px_1fr_120px_150px_92px] items-center gap-2 border-b border-line-soft px-2 pb-1 text-[10px] font-semibold tracking-wide text-muted uppercase">
                  <span>ID</span>
                  <span>제목</span>
                  <span>선행</span>
                  <span>담당 · repo · 공수</span>
                  <span>상태</span>
                </div>
                <ul className="flex flex-col">
                  {current.children.items.map((child) => (
                    <li key={child.key}>
                      <button
                        onClick={() => setStack((items) => [...items, child.open()])}
                        className="grid w-full cursor-pointer grid-cols-[36px_1fr_120px_150px_92px] items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-line-soft"
                      >
                        <span className="font-mono text-[11px] text-faint">{child.key}</span>
                        <span className="min-w-0 truncate text-[13px]">{child.label}</span>
                        <span className="truncate text-[11px]">
                          {child.dependsOn.length === 0 ? (
                            <span className="text-faint">-</span>
                          ) : (
                            child.dependsOn.map((id) => (
                              <span
                                key={id}
                                className={`mr-1 rounded px-1 ${
                                  child.blockedBy.includes(id) ? 'bg-amber-100 text-amber-900' : 'bg-line-soft text-muted'
                                }`}
                                title={child.blockedBy.includes(id) ? `${id} 미완 — 대기` : `${id} 완료`}
                              >
                                {child.blockedBy.includes(id) ? `⛔ ${id}` : id}
                              </span>
                            ))
                          )}
                        </span>
                        <span className="truncate text-[11px] text-faint">{child.hint || '-'}</span>
                        <span
                          className={`justify-self-start rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide uppercase ${LOZENGE[child.status] ?? LOZENGE.unknown}`}
                        >
                          {child.statusToken || child.status}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                  </>
                )}
              </div>
            )}

            {/* 좁은 화면에는 목록 대신 선택 상자 — 가로 탭은 문서가 열 개를 넘으면 못 쓴다. */}
            {mainTab === 'docs' && current.docs.length > 0 && (
              <div className="border-b border-line px-4 py-2 lg:hidden">
                <select
                  className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-xs"
                  value={activePath ?? ''}
                  onChange={(event) => setActivePath(event.target.value)}
                >
                  {current.docs.map((entry) => (
                    <option key={entry.path} value={entry.path}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {current.body && (
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-16">
                <div className="mb-3 font-mono text-[11px] text-faint">{current.body.label}</div>
                {current.body.header}
                {current.body.text === undefined ? current.body.content : splitBodySections(current.body.text).map((section, index) => {
                  if (!isHtmlBody(section)) return <Markdown key={index} text={section} />;
                  const { name, rest } = splitAttachmentHeading(section);
                  return <HtmlPreview key={index} name={name} html={rest} />;
                })}
                {current.body.footer}
              </div>
            )}

            <div
              className={`min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-16 ${
                mainTab === 'docs' && !current.body ? '' : 'hidden'
              }`}
            >
              {current.docs.length === 0 && <div className="text-muted">연결된 문서가 없다.</div>}
              {docError && <div className="text-amber-800">{docError}</div>}
              {activePath && !doc && !docError && <div className="text-muted">불러오는 중…</div>}
              {doc && (
                <>
                  <div className="mb-3 font-mono text-[11px] text-faint">
                    {doc.path} · 수정 {doc.updatedAt ?? '-'}
                  </div>
                  {/* 역기획서면 일정을 여기서 고친다. 저장된 본문으로 문서만 다시 그린다 — 색인은 다음 실행이 따라온다. */}
                  {QaBriefSchedule.isBrief(doc.path) && (
                    <QaScheduleEditor doc={doc} onSaved={(text) => setDoc({ ...doc, text })} />
                  )}
                  {/* 문서 탭에는 색인된 `.md`만 온다. 본문이 `<script setup>`·`<html dir>`을 언급만 해도
                      HTML로 판정되면 원문이 iframe에 날것으로 뭉개지므로 HTML 판정을 걸지 않는다. */}
                  <Markdown text={DevRequestLink.linkify(doc.text)} asset={{ source: doc.source, path: doc.path }} />
                </>
              )}
            </div>
          </div>

          {/* 세부 정보 — 지라의 오른쪽 필드 패널 */}
          <aside className="overflow-y-auto border-t border-line bg-canvas px-4 py-4 lg:border-t-0 lg:border-l">
            <div className="mb-2 text-[11px] font-semibold tracking-wide text-muted uppercase">세부 정보</div>
            <dl className="flex flex-col gap-2.5">
              {current.meta
                .filter((row) => row.value !== null && row.value !== '' && row.value !== undefined)
                .map((row) => (
                  <div key={row.label} className="grid grid-cols-[84px_1fr] items-start gap-2">
                    <dt className="pt-0.5 text-[11.5px] text-muted">{row.label}</dt>
                    <dd className="m-0 text-[12.5px] leading-relaxed break-words">{row.value}</dd>
                  </div>
                ))}
            </dl>
          </aside>
        </div>
      </aside>
    </>
  );
}
